import { API, Auth } from "aws-amplify";
import { Formik } from "formik";
import React from "react";
import Button from "_shared/components/Button/Button";
import InputField from "_shared/components/InputField/InputField";
import { useModal } from "_shared/contexts/ModalContext";
import CustomModal from "../../../_shared/components/Modal/Modal";
import styles from "./HelpModal.module.scss";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<Props> = ({ onClose, isOpen }) => {
  const { onErrorMsg, onSuccessMsg } = useModal();

  async function handleSubmit({
    subject,
    message,
  }: {
    subject: string;
    message: string;
  }) {
    const userData = (await Auth.currentSession()).getIdToken().payload;
    try {
      await API.post("auth", "/cognito/zendesk", {
        body: {
          user: userData,
          subject: subject,
          message: message,
        },
      });
      onSuccessMsg(`
        We've received your message and sent a confirmation email
        to ${userData.email}. Someone from our team
        will follow up within the next 48 hours.
      `);
      onClose();
    } catch (err: any) {
      onErrorMsg(
        "There was a problem submitting your message. Please try again.",
      );
    }
  }

  return (
    <CustomModal
      open={isOpen}
      onClose={onClose}
      title="Need help?"
      subtitle="Send us a message and someone on our team will follow up with you."
    >
      <div className={styles.container}>
        <Formik
          initialValues={{ subject: "", message: "" }}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, isSubmitting, values, handleChange }) => (
            <>
              <form className={styles.form} onSubmit={handleSubmit}>
                <InputField
                  name="subject"
                  placeholder="Subject"
                  label="Subject"
                  dark
                />
                <textarea
                  value={values.message}
                  onChange={handleChange}
                  name="message"
                  className={styles.textInput}
                  placeholder="How can we help?"
                />
                <div className="footer-buttons">
                  <Button onClick={onClose} variant="secondary">
                    Cancel
                  </Button>
                  <Button type="submit" loading={isSubmitting}>
                    Send
                  </Button>
                </div>
              </form>
            </>
          )}
        </Formik>
      </div>
    </CustomModal>
  );
};

export default React.memo(HelpModal);
